import React, { useMemo } from 'react';

import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { Alert, Box } from '@mui/material';

import { useGetAccountOfferIds, useGetAccountOpportunities } from '~/hooks/apis/offers/useGetOffer';

import AccountOpportunityTile from './AccountOpportunityTile';
import RelatedListCard from './RelatedListCard';
import {
  OFFER_LINKS_UNAVAILABLE_MESSAGE,
  RELATED_PREVIEW_LIMIT,
  accountPath,
  sortOpportunitiesByLatestDate,
} from './accountRelatedModel';
import { useAccountOfferId } from './useAccountOfferId';

interface Props {
  accountId: string;
}

export default function AccountOpportunitiesCard({ accountId }: Props) {
  const offerId = useAccountOfferId();
  const { data: opportunities, isLoading, error } = useGetAccountOpportunities(accountId);
  const { data: offerIds, error: offerIdsError } = useGetAccountOfferIds(accountId);
  const preview = useMemo(
    () => sortOpportunitiesByLatestDate(opportunities ?? []).slice(0, RELATED_PREVIEW_LIMIT),
    [opportunities],
  );

  return (
    <RelatedListCard
      count={opportunities?.length ?? 0}
      error={error}
      icon={<EmojiEventsIcon color="action" />}
      isLoading={isLoading}
      title="Opportunities"
      viewAllTo={accountPath(accountId, offerId, '/opportunities')}
    >
      {offerIdsError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {OFFER_LINKS_UNAVAILABLE_MESSAGE}
        </Alert>
      )}
      <Box
        sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' } }}
      >
        {preview.map((opportunity) => (
          <AccountOpportunityTile
            key={opportunity.id}
            hasOffer={!!offerIds?.has(opportunity.id)}
            opportunity={opportunity}
          />
        ))}
      </Box>
    </RelatedListCard>
  );
}
